export const BUSINESS_VERIFICATION_REPORT_TYPE = 'BusinessVerificationReport'
export const WORKFLOW_VERIFICATION_TYPE = 'WorkflowVerification'

export const INTROSPECTION_QUERY = `
query DevQueryToolSchemaIntrospection {
  __schema {
    types {
      kind
      name
      fields {
        name
        type {
          ...TypeRef
        }
      }
    }
  }
}

fragment TypeRef on __Type {
  kind
  name
  ofType {
    kind
    name
    ofType {
      kind
      name
      ofType {
        kind
        name
        ofType {
          kind
          name
          ofType {
            kind
            name
            ofType {
              kind
              name
              ofType {
                kind
                name
              }
            }
          }
        }
      }
    }
  }
}
`

let cachedTypeMap
let cachedTypeMapPromise

const indentBlock = (value, spaces = 4) => value
  .split('\n')
  .map((line) => `${' '.repeat(spaces)}${line}`)
  .join('\n')

const getNamedType = (typeRef) => {
  let current = typeRef
  while (current?.ofType) {
    current = current.ofType
  }
  return current
}

const getTypeFields = ({ typeMap, typeName }) => typeMap[typeName]?.fields || []

const isLeafType = ({ typeMap, typeRef }) => {
  const namedType = getNamedType(typeRef)
  if (!namedType?.name) {
    return false
  }

  const resolvedKind = namedType.kind || typeMap[namedType.name]?.kind
  return resolvedKind === 'SCALAR' || resolvedKind === 'ENUM'
}

const isObjectType = ({ typeMap, typeRef }) => {
  const namedType = getNamedType(typeRef)
  if (!namedType?.name) {
    return false
  }

  const resolvedKind = namedType.kind || typeMap[namedType.name]?.kind
  return resolvedKind === 'OBJECT'
}

const getLeafFieldNamesForType = ({ typeMap, typeName }) => getTypeFields({ typeMap, typeName })
  .filter((field) => isLeafType({ typeMap, typeRef: field.type }))
  .map((field) => field.name)

const buildSelectionForType = ({
  typeMap,
  typeName,
  includeFields,
  depth = 0,
  maxDepth = 8,
  seenTypes = new Set(),
}) => {
  const typeFields = getTypeFields({ typeMap, typeName })
  if (!typeFields.length) {
    return ''
  }

  return typeFields
    .filter((field) => !includeFields || includeFields.has(field.name))
    .map((field) => {
      if (isLeafType({ typeMap, typeRef: field.type })) {
        return field.name
      }

      const namedType = getNamedType(field.type)
      if (!isObjectType({ typeMap, typeRef: field.type }) || !namedType?.name) {
        return null
      }

      if (depth >= maxDepth || seenTypes.has(namedType.name)) {
        return null
      }

      const nestedSelection = buildSelectionForType({
        typeMap,
        typeName: namedType.name,
        depth: depth + 1,
        maxDepth,
        seenTypes: new Set([...seenTypes, namedType.name]),
      })

      if (!nestedSelection) {
        return null
      }

      return `${field.name} {\n${indentBlock(nestedSelection)}\n}`
    })
    .filter(Boolean)
    .join('\n')
}

export const buildTypeMap = (schema) => Object.fromEntries(
  (schema?.types || [])
    .filter((type) => type?.name)
    .map((type) => [type.name, type]),
)

export const getBusinessVerificationDomains = (typeMap) => {
  return getTypeFields({ typeMap, typeName: BUSINESS_VERIFICATION_REPORT_TYPE })
    .filter((field) => isObjectType({ typeMap, typeRef: field.type }))
    .map((field) => field.name)
}

export const buildBusinessVerificationQuery = ({ typeMap, selectedDomains }) => {
  const reportType = typeMap[BUSINESS_VERIFICATION_REPORT_TYPE]
  if (!reportType?.fields?.length) {
    throw new Error(`GraphQL type "${BUSINESS_VERIFICATION_REPORT_TYPE}" was not found in schema introspection`)
  }

  const selectedFields = new Set([
    ...getLeafFieldNamesForType({
      typeMap,
      typeName: BUSINESS_VERIFICATION_REPORT_TYPE,
    }),
    ...selectedDomains,
  ])

  const selection = buildSelectionForType({
    typeMap,
    typeName: BUSINESS_VERIFICATION_REPORT_TYPE,
    includeFields: selectedFields,
    seenTypes: new Set([BUSINESS_VERIFICATION_REPORT_TYPE]),
  })

  return `query GetBusinessVerificationByDomains($orderId: ID!) {
  getBusinessVerificationByDomains(orderId: $orderId) {
${indentBlock(selection, 4)}
  }
}`
}

export const buildWorkflowVerificationQuery = ({ typeMap }) => {
  const workflowType = typeMap[WORKFLOW_VERIFICATION_TYPE]
  if (!workflowType?.fields?.length) {
    throw new Error(`GraphQL type "${WORKFLOW_VERIFICATION_TYPE}" was not found in schema introspection`)
  }

  const selection = buildSelectionForType({
    typeMap,
    typeName: WORKFLOW_VERIFICATION_TYPE,
    seenTypes: new Set([WORKFLOW_VERIFICATION_TYPE]),
  })

  return `query GetWorkflowVerification($orderId: ID!) {
  getWorkflowVerification(orderId: $orderId) {
${indentBlock(selection, 4)}
  }
}`
}

export const loadDevQueryTypeMap = async (executeGraphql) => {
  if (cachedTypeMap) {
    return cachedTypeMap
  }

  if (!cachedTypeMapPromise) {
    cachedTypeMapPromise = executeGraphql(INTROSPECTION_QUERY)
      .then((result) => {
        const nextTypeMap = buildTypeMap(result?.data?.__schema)
        if (
          !nextTypeMap[BUSINESS_VERIFICATION_REPORT_TYPE]
          || !nextTypeMap[WORKFLOW_VERIFICATION_TYPE]
        ) {
          throw new Error('Required GraphQL types were not returned by introspection')
        }

        cachedTypeMap = nextTypeMap
        return nextTypeMap
      })
      .finally(() => {
        cachedTypeMapPromise = null
      })
  }

  return cachedTypeMapPromise
}
